import { useState, useRef, useMemo } from 'react'
import { Box, ChevronDown, Search, X } from 'lucide-react'
import Popover from './Popover'
import type { Product } from '../types'

interface ProductPickerProps {
  value: string | undefined
  onChange: (productId: string | undefined) => void
  products: Product[]
  placeholder?: string
}

const optionClass = 'w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none transition-colors'

/**
 * Searchable dropdown over the products list. Filters client-side by name
 * and vendor. Always clearable since `productId` on a project is optional.
 * Products are passed in as a prop because callers (AddProject, ProjectDetail)
 * already load them via `useData`, so a per-picker fetch would be wasteful.
 */
export default function ProductPicker({
  value,
  onChange,
  products,
  placeholder = 'No product',
}: ProductPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const selected = useMemo(
    () => (value ? products.find(p => p.id === value) : undefined),
    [value, products],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return products
    return products.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.vendor || '').toLowerCase().includes(q),
    )
  }, [products, query])

  const close = () => {
    setOpen(false)
    setQuery('')
  }

  const select = (id: string | undefined) => {
    onChange(id)
    close()
  }

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(undefined)
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={`w-full flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-danone-cyan transition-colors ${
          selected ? 'border-slate-200 bg-white text-slate-800' : 'border-slate-200 bg-slate-50 text-slate-500'
        }`}
      >
        {selected ? (
          <>
            <span className="w-6 h-6 rounded-full bg-cyan-50 text-cyan-700 flex items-center justify-center flex-shrink-0">
              <Box className="w-3 h-3" />
            </span>
            <span className="flex-1 text-left truncate">
              {selected.name}
              {selected.vendor && <span className="text-slate-400"> · {selected.vendor}</span>}
            </span>
            <span
              onClick={clear}
              className="text-slate-400 hover:text-slate-600 flex-shrink-0"
              aria-label="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          </>
        ) : (
          <span className="flex-1 text-left">{placeholder}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      <Popover open={open} anchorRef={ref} onClose={close} width="anchor" className="min-w-[16rem]">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 flex-shrink-0">
          <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <input
            autoFocus
            data-popover-search
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name or vendor"
            aria-label="Search by name or vendor"
            className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-slate-400"
          />
        </div>
        <div role="listbox" className="max-h-72 min-h-0 overflow-y-auto py-1">
          <button
            type="button"
            role="option"
            aria-selected={!value}
            data-option
            onClick={() => select(undefined)}
            className={`${optionClass} ${!value ? 'bg-cyan-50' : ''}`}
          >
            <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0">
              <X className="w-3 h-3" />
            </span>
            <span className="flex-1 text-sm text-slate-500 italic">No product</span>
          </button>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">
              {query ? 'No matches' : 'No products yet'}
            </div>
          )}
          {filtered.map(p => {
            const isSelected = value === p.id
            return (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-option
                onClick={() => select(p.id)}
                className={`${optionClass} ${isSelected ? 'bg-cyan-50' : ''}`}
              >
                <span className="w-7 h-7 rounded-full bg-cyan-50 text-cyan-700 flex items-center justify-center flex-shrink-0">
                  <Box className="w-3.5 h-3.5" />
                </span>
                <span className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{p.name}</div>
                  {p.vendor && <div className="text-[11px] text-slate-400 truncate">{p.vendor}</div>}
                </span>
              </button>
            )
          })}
        </div>
      </Popover>
    </div>
  )
}
